import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { useTranslation } from "../../../localization";

// Shared rendering + share-text for a single locked (live/past) match's predictions.
// Used by both the per-match dialog and the page-level "Show all predictions" inline
// expansion so the two stay in sync. A fixture is "scored" once it has a score, at which
// point each prediction also carries `points` / `points_breakdown`.

function isScored(fixture) {
  return "home_score" in fixture;
}

// Predictions ordered the way the bot shows them: by points (desc) once the match is
// scored, otherwise alphabetically by player name.
export function sortedPredictions(fixture) {
  var predictions = (fixture.predictions || []).slice();
  if (isScored(fixture)) {
    predictions.sort((a, b) => (b.points || 0) - (a.points || 0));
  } else {
    predictions.sort((a, b) => a.name.localeCompare(b.name));
  }
  return predictions;
}

// Predictions rendered as a table: Player | Prediction | Points | Points Info.
// In admin edit mode (`editing`) the Prediction cell becomes two number inputs
// bound to `draft[username]`, edited via `onDraftChange(username, field, value)`.
// Points / Points Info stay read-only since they recompute server-side on save.
export function PredictionsTable({
  fixture,
  className,
  editing,
  draft,
  onDraftChange,
}) {
  var t = useTranslation();
  var predictions = sortedPredictions(fixture);
  if (predictions.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {t.predictions.noPredictions}
      </Typography>
    );
  }
  var scored = isScored(fixture);
  function draftValue(username, field) {
    var entry = (draft && draft[username]) || {};
    return entry[field] === undefined || entry[field] === null
      ? ""
      : entry[field];
  }
  return (
    <Table size="small" className={className}>
      <TableHead>
        <TableRow>
          <TableCell>{t.predictions.player}</TableCell>
          <TableCell>{t.predictions.prediction}</TableCell>
          {scored ? <TableCell>{t.predictions.points}</TableCell> : null}
          {scored ? <TableCell>{t.predictions.pointsInfo}</TableCell> : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {predictions.map(p => (
          <TableRow key={p.username}>
            <TableCell>{p.name}</TableCell>
            <TableCell>
              {editing ? (
                <span>
                  <TextField
                    type="number"
                    size="small"
                    style={{ width: 56 }}
                    value={draftValue(p.username, "home_score")}
                    onChange={e =>
                      onDraftChange(p.username, "home_score", e.target.value)
                    }
                  />
                  {" - "}
                  <TextField
                    type="number"
                    size="small"
                    style={{ width: 56 }}
                    value={draftValue(p.username, "away_score")}
                    onChange={e =>
                      onDraftChange(p.username, "away_score", e.target.value)
                    }
                  />
                </span>
              ) : (
                <span>
                  {p.home_score} - {p.away_score}
                </span>
              )}
            </TableCell>
            {scored ? (
              <TableCell>
                {p.points !== undefined && p.points !== null ? p.points : ""}
              </TableCell>
            ) : null}
            {scored ? <TableCell>{p.points_breakdown || ""}</TableCell> : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// A short header describing the match's teams + score/status, reused by the share text.
// `t` is the active translation bundle, used for the "LIVE"/"Reg" labels.
function matchHeader(fixture, t) {
  if (fixture.live) {
    return (
      fixture.home_team +
      " " +
      fixture.home_score +
      " - " +
      fixture.away_score +
      " " +
      fixture.away_team +
      " (" +
      t.share.liveLabel +
      " - " +
      (fixture.display_clock || fixture.status_detail || "") +
      ")"
    );
  }
  if (isScored(fixture)) {
    var header =
      fixture.home_team +
      " " +
      fixture.home_score +
      " - " +
      fixture.away_score +
      " " +
      fixture.away_team;
    if (fixture.has_post_reg_goals) {
      header +=
        " (" +
        t.share.regLabel +
        ": " +
        fixture.home_score_reg +
        " - " +
        fixture.away_score_reg +
        ")";
    }
    if (fixture.date_display) {
      header += " (" + fixture.date_display + ")";
    }
    return header;
  }
  return fixture.home_team + " - " + fixture.away_team;
}

// Plain-text block for one match, formatted like the bot's `recent`/`active` output so it
// pastes cleanly into chat. `t` is the active translation bundle; `leagueLabel` is optional.
export function matchShareText(fixture, t, leagueLabel) {
  var lines = [];
  // With a league label, matches nest under it; without one (the whole-page share) the
  // match title sits flush left and its players indent under it.
  var headerIndent = leagueLabel ? "    " : "";
  var playerIndent = leagueLabel ? "        " : "    ";
  if (leagueLabel) {
    lines.push(leagueLabel);
  }
  lines.push(headerIndent + matchHeader(fixture, t));
  var scored = isScored(fixture);
  var last = null;
  var place = 0;
  sortedPredictions(fixture).forEach(p => {
    var points = p.points || 0;
    if (scored) {
      if (last === null || points !== last) {
        place += 1;
      }
      last = points;
      var pointsSuffix =
        p.points !== undefined && p.points !== null
          ? " (" +
            p.points +
            t.share.ptsSuffix +
            (p.points_breakdown ? ": " + p.points_breakdown : "") +
            ")"
          : "";
      lines.push(
        playerIndent +
          place +
          ". " +
          p.name +
          ": " +
          p.home_score +
          " - " +
          p.away_score +
          pointsSuffix,
      );
    } else {
      lines.push(
        playerIndent + p.name + ": " + p.home_score + " - " + p.away_score,
      );
    }
  });
  return lines.join("\n");
}

// Copy text to the clipboard, with a fallback for non-secure contexts (the app is served
// over http on the LAN, where navigator.clipboard is unavailable). Mirrors the helper in
// Leaderboards.js. Calls onDone() once the copy succeeds.
export function copyToClipboard(text, onDone) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(
      () => onDone && onDone(),
      error => console.debug(error),
    );
    return;
  }
  var textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  // Focus before selecting: when invoked from inside a focus-trapped dialog the textarea
  // must hold focus for execCommand("copy") to read its selection.
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  try {
    document.execCommand("copy");
    onDone && onDone();
  } catch (error) {
    console.debug(error);
  }
  document.body.removeChild(textarea);
}
